import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQuestionCircle } from '@fortawesome/pro-solid-svg-icons/faQuestionCircle';

import React, { useCallback, useContext } from 'react';
import classnames from 'clsx';
import { FormattedMessage } from 'react-intl';

import { Checkbox } from 'ui-components/components/checkbox';
import { DimensionInput } from 'ui-components/components/dimension-input';
import { Divider } from 'ui-components/components/divider';
import { InfoIcon } from 'ui-components/components/info-icon';
import { INLINE_MESSAGE_TYPES } from 'ui-components/components/inline-message';
import { Input, version2 } from 'ui-components/components/input';
import { Text, TextType } from 'ui-components/components/text';
import { Tooltip } from 'ui-components/components/tooltip';
import { WeightInput } from 'ui-components/components/weight-input';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { BYOLRestrictionsBanner } from '../../../../components/byol-banner';
import { DangerousGoodsForm } from '../../../../components/dangerous-goods/dangerous-goods-form';
import { MsdsUpload } from '../../../../components/dangerous-goods/msds-upload/msds-upload';
import { useEnableDangerousGoods } from '../../../../feature-flags/hooks/use-enable-dangerous-goods';
import { useEnableFulfillmentRates } from '../../../../feature-flags/hooks/use-enable-fulfillment-rates';
import { useEnableProductItemWeightAndDimensions } from '../../../../feature-flags/hooks/use-enable-product-item-weight-and-dimensions';
import { useIsAutoSplitEnabledForSeller } from '../../../../hooks/use-is-auto-splitting-enabled-for-seller';
import { useIsDeliveryOptionsAnz } from '../../../../pages/settings/stores/selected-store/delivery-options-settings/hooks';
import { BundleContext } from '../../bundle-context';
import { ProductDetailsContext } from '../../context';
import { messages } from '../../messages';
import style from '../../style.module.css';
import utilityStyle from '../../../../css-variables/utilities.module.css';
import { AutoSplitRadioButtons } from '../auto-split-radio-buttons';
import { ProductDetailsDimensionsPresetGroupDisplay } from '../dimensions-inherited-display';
import { ProductDetailsEnabledCascadingDropdowns } from '../enabled-cascading-dropdowns';
import { PackagingOptionsDropdown } from '../packaging-options-dropdown/packaging-options-dropdown';
import { ProductDetailsPresetGroupDropdown } from '../preset-group-dropdown';
import { ProductFee } from '../product-fee';
import { ProductDetailsWeightPresetGroupDisplay } from '../weight-inherited-display';

export const ProductDetailsShippingDefaults = () => {
  const {
    productId,
    userHasPermission,
    weightValue: productWeightValue,
    setWeightValue,
    weightUnit: productWeightUnit,
    productWarehouseLocation,
    setProductWarehouseLocation,
    productFulfillmentSku,
    setProductFulfillmentSku,
    dimensions,
    setDimensions,
    sizeUnit,
    autoSplitOption,
    setAutoSplitOption,
    autoSplitCustomQuantity,
    setAutoSplitCustomQuantity,
    productPrice,
    setProductPrice,
    enableProductPrice,
    setEnableProductPrice,
    productDangerousGoods,
    setProductDangerousGoods,
    productMsdsFile,
    setProductMsdsFile,
    validationErrors,
    productItemWeight,
    setProductItemWeight,
    productItemDimensions,
    setProductItemDimensions,
  } = useContext(ProductDetailsContext);
  const { isBundle, componentsWeight } = useContext(BundleContext);

  const fulfillmentRatesEnabled = useEnableFulfillmentRates();
  const isAutoSplitEnabled = useIsAutoSplitEnabledForSeller();
  const weightValue = isBundle ? componentsWeight?.value : productWeightValue;
  const weightUnit = isBundle ? componentsWeight?.unit : productWeightUnit;
  const isDeliveryOptionsAnz = useIsDeliveryOptionsAnz();

  const onToggleDangerousGoods = useCallback(
    (value) => {
      setProductDangerousGoods(
        value
          ? {
              dangerousAmount: {
                amount: 0,
                unit: 'g',
              },
            }
          : null,
      );
    },
    [setProductDangerousGoods],
  );

  const isDangerousGoodsOptionEnabled = useEnableDangerousGoods();
  const isProductItemWeightAndDimensionsEnabled = useEnableProductItemWeightAndDimensions();

  const isDangerousGoodsEnabled = !!productDangerousGoods;

  return (
    <>
      <div className={style['shipping-defaults-header-wrapper']}>
        <div className={style['shipping-defaults-header-text']}>
          <FormattedMessage {...messages.shippingDefaults} />
        </div>
        <div className={style['preset-wrapper']}>
          <ProductDetailsPresetGroupDropdown />
        </div>
      </div>
      <div className={style['byol-banner']}>
        <BYOLRestrictionsBanner contentText={messages.BYOLRestrictionsBannerText} />
      </div>
      <div className={style['defaults-info-message']}>
        <Text textType='caption' value={messages.shippingDefaultsSubHeader} />
      </div>
      <div className={style['shipping-options']}>
        <ProductDetailsEnabledCascadingDropdowns />
        <ProductDetailsEnabledCascadingDropdowns isInternational />
      </div>
      <div className={style['more-options']}>
        <div className={style.label}>
          <FormattedMessage {...messages.packageWeight} />{' '}
          {isBundle ? (
            <InfoIcon color={THEME_CONSTANTS.unstyled} tooltipText={<FormattedMessage {...messages.bundleComponentWeight} />} />
          ) : null}
        </div>
        <div className={style['rightside-label']}>
          <FormattedMessage {...messages.warehouseLocation} />
        </div>
        <div className={style['weight-wrapper']}>
          <WeightInput
            disabled={!userHasPermission || isBundle}
            inputClassName={classnames(style['weight-input'], utilityStyle['input-border'])}
            onChange={setWeightValue}
            unit={weightUnit}
            weight={weightValue || 0}
          />
          <ProductDetailsWeightPresetGroupDisplay />
        </div>
        <div>
          <Input
            className={classnames(style['input-wrapper'], utilityStyle['input-border'])}
            data-testid='warehouse-location'
            disabled={!userHasPermission}
            inlineMessage={validationErrors?.warehouseLocation?.message}
            inlineMessageType={validationErrors?.warehouseLocation?.message ? INLINE_MESSAGE_TYPES.error : undefined}
            onChange={(event) => setProductWarehouseLocation(event.target.value)}
            value={productWarehouseLocation}
            version={version2}
          />
        </div>
        <div className={classnames(style.label, style['label-with-tooltip'])}>
          <FormattedMessage {...messages.packageDimensions} />
          {isDeliveryOptionsAnz ? <InfoIcon tooltipText={<FormattedMessage {...messages.shippingDefaultsTooltip} />} /> : null}
        </div>
        <div className={style['rightside-label']}>
          <FormattedMessage {...messages.fulfillmentSku} />
        </div>
        <div className={style['size-wrapper']}>
          <DimensionInput
            dimensions={dimensions}
            disabled={!userHasPermission}
            inputClassName={classnames(style['size-input'], utilityStyle['input-border'])}
            onChange={setDimensions}
            unit={sizeUnit}
          />
          <ProductDetailsDimensionsPresetGroupDisplay />
        </div>
        <div>
          <Input
            className={classnames(style['input-wrapper'], utilityStyle['input-border'])}
            data-testid='fulfillment-sku'
            disabled={!userHasPermission}
            onChange={setProductFulfillmentSku}
            value={productFulfillmentSku}
          />
        </div>
        <PackagingOptionsDropdown disabled={!userHasPermission} />
        {isProductItemWeightAndDimensionsEnabled ? (
          <>
            <div className={style.label}>
              <FormattedMessage {...messages.packageItemWeight} />
            </div>
            <div className={style['rightside-label']}>
              <FormattedMessage {...messages.packageItemDimensions} />
            </div>
            <div className={style['weight-wrapper']}>
              <WeightInput
                disabled={!userHasPermission || isBundle}
                inputClassName={classnames(style['weight-input'], utilityStyle['input-border'])}
                onChange={(weight, unit) =>
                  setProductItemWeight({
                    value: weight,
                    unit: unit || productItemWeight.unit || weightUnit,
                  })
                }
                unit={productItemWeight.unit || weightUnit}
                weight={productItemWeight.value || 0}
              />
            </div>
            <div className={style['size-wrapper']}>
              <DimensionInput
                dimensions={productItemDimensions}
                disabled={!userHasPermission}
                inputClassName={classnames(style['size-input'], utilityStyle['input-border'])}
                onChange={(dims) =>
                  setProductItemDimensions({
                    ...dims,
                    unit: dims.unit ?? productItemDimensions.unit ?? sizeUnit,
                  })
                }
                unit={productItemDimensions.unit || sizeUnit}
              />
            </div>
          </>
        ) : null}
        {isAutoSplitEnabled ? (
          <>
            <div className={style['rightside-label']}>
              <FormattedMessage {...messages.autoSplitOption} />
              <Tooltip position='top' text={<FormattedMessage {...messages.autoSplitTooltip} />}>
                <span>
                  <FontAwesomeIcon className={style.icon} icon={faQuestionCircle} />
                </span>
              </Tooltip>
            </div>
            <div />
            <AutoSplitRadioButtons
              currentQuantity={autoSplitCustomQuantity}
              disabled={!userHasPermission}
              onChange={setAutoSplitOption}
              selectedSplitOption={autoSplitOption}
              setAutoSplitCustomQty={setAutoSplitCustomQuantity}
            />
          </>
        ) : null}
      </div>
      {fulfillmentRatesEnabled ? (
        <ProductFee
          enableProductPrice={enableProductPrice}
          initialProductPrice={productPrice}
          setEnableProductPrice={setEnableProductPrice}
          setProductPrice={setProductPrice}
          userHasPermission={userHasPermission}
        />
      ) : null}
      {isDangerousGoodsOptionEnabled ? (
        <div className={style['dg-section']}>
          <Text className={style['dg-section-title']} textType={TextType.H4} value={messages.dangerousGoodsSectionTitle} />
          <div className={style['dg-section-set-dg-checkbox']}>
            <Checkbox
              checked={isDangerousGoodsEnabled}
              label={<FormattedMessage {...messages.dangerousGoodsSectionSubtitle} />}
              onChange={onToggleDangerousGoods}
            />
          </div>
          {isDangerousGoodsEnabled ? (
            <div className={style['dangerous-goods-details']}>
              {Boolean(productId) && (
                <>
                  <MsdsUpload
                    currentMsdsFile={productMsdsFile}
                    isDisabled={!userHasPermission}
                    productId={productId}
                    setCurrentMsdsFile={setProductMsdsFile}
                  />
                  <Divider className={style.divider} orientation='horizontal' />
                </>
              )}
              <DangerousGoodsForm onChange={setProductDangerousGoods} value={productDangerousGoods} />
            </div>
          ) : null}
        </div>
      ) : null}
    </>
  );
};
